<template>
  <div>
    <form-section :title="$t('Integrations_Settings_BasicSettings')">
      <rest-form-item
        :hint="$t('Integrations_Field_Name_Description')"
        :label="$t('Integrations_Field_Name')"
        prop="name"
      >
        <el-input
          v-model="model.name"
          :placeholder="$t('Integrations_Field_Name')"
        />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Settings_WebhookMessageSettings')">
      <rest-form-item
        :label="$t('Integrations_Field_WebhookUrl')"
        prop="params.url"
      >
        <el-input
          v-model="model.params.url"
          :placeholder="$t('Integrations_Field_WebhookUrl')"
        />
      </rest-form-item>

      <rest-form-item
        :label="$t('Integrations_Field_Token')"
        prop="params.bearer_token"
      >
        <el-input
          v-model="model.params.bearer_token"
          :placeholder="$t('Authentication_Type_BearerToken')"
        />
      </rest-form-item>

      <el-row>
        <el-col :span="12" class="token-wrapper">
          <rest-form-item
            :label="$t('Integrations_Field_JWT_Key')"
            prop="params.jwt_key"
          >
            <el-input
              v-model="secret"
              :class="{ blurred: !showToken }"
              :disabled="true"
              type="text"
            />
            <icon
              v-if="!showToken"
              :alt="$t('ApiIntegration_Button_AppTokenReveal')"
              :title="$t('ApiIntegration_Button_AppTokenReveal')"
              glyph="eye"
              size="20"
              @click="showToken = true"
            />
            <icon
              v-else
              :alt="$t('ApiIntegration_Button_AppTokenHide')"
              :title="$t('ApiIntegration_Button_AppTokenHide')"
              glyph="eye-crossed"
              size="20"
              @click="showToken = false"
            />
          </rest-form-item>
        </el-col>
        <el-col :span="8">
          <rest-form-item :label="' '" prop="params.jwt_key">
            <el-button @click="handleGenerateButton()"
              >{{ $t('Integrations_Field_JWT_Button') }}
            </el-button>
          </rest-form-item>
        </el-col>
      </el-row>
      <el-row v-if="tokenError !== false">
        <el-col :span="12">
          <el-alert
            :title="$t('Integrations_Field_JWT_Error')"
            show-icon
            type="error"
          >
          </el-alert>
        </el-col>
      </el-row>
    </form-section>

    <form-section :title="$t('Integrations_Settings_WebhookConsentSettings')">
      <rest-form-item prop="params.enable_consent">
        <toggle
          v-model="model.params.enable_consent"
          :text="$t('Integrations_Field_Enabled')"
        />
      </rest-form-item>

      <rest-form-item
        v-if="model.params.enable_consent || false"
        :label="$t('Integrations_Field_WebhookConsentUrl')"
        prop="params.consent_url"
      >
        <el-input
          v-model="model.params.consent_url"
          :placeholder="$t('Integrations_Field_WebhookConsentUrl')"
        />
      </rest-form-item>

      <rest-form-item
        v-if="model.params.enable_consent || false"
        :label="$t('Integrations_Field_WebhookConsentAuthUrl')"
        prop="params.consent_auth_url"
      >
        <el-input
          v-model="model.params.consent_auth_url"
          :placeholder="$t('Integrations_Field_WebhookConsentAuthUrl')"
        />
      </rest-form-item>

      <rest-form-item
        v-if="model.params.enable_consent || false"
        :label="$t('Integrations_Field_WebhookConsentClientID')"
        prop="params.consent_client_id"
      >
        <el-input
          v-model="model.params.consent_client_id"
          :placeholder="$t('Integrations_Field_WebhookConsentClientID')"
        />
      </rest-form-item>

      <rest-form-item
        v-if="model.params.enable_consent || false"
        :label="$t('Integrations_Field_WebhookConsentClientSecret')"
        prop="params.consent_client_secret"
      >
        <el-input
          v-model="model.params.consent_client_secret"
          :placeholder="$t('Integrations_Field_WebhookConsentClientSecret')"
        />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Settings_WebhookMessageEvents')">
      <el-row>
        <el-col :span="12">
          <p class="list-title">
            {{ $t('Integrations_Webhook_Title_MessageEvents') }}
          </p>
          <ToggleList
            :data="messageEventsList"
            :model="model.params.message_events"
          />
        </el-col>

        <el-col :span="12">
          <p class="list-title">
            {{ $t('Integrations_Webhook_Title_MessageTypes') }}
          </p>
          <ToggleList
            :data="messageTypesList"
            :model="model.params.message_types"
          />
        </el-col>
      </el-row>
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import ToggleList from '@/common/components/form/ToggleList'
import { isValidUrl } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import Toggle from '@/common/components/form/Toggle.vue'

export default {
  name: 'OpapWebhookSettings',

  components: {
    Toggle,
    FormSection,
    RestFormItem,
    ToggleList
  },

  props: {
    model: {
      type: Object,
      required: true
    }
  },

  created() {
    if (this.isCreatePage) {
      this.$set(this.model.params, 'message_types', {
        ios_push: 0,
        android_push: 0,
        web_push: 0,
        inapp: 0
      })

      this.$set(this.model.params, 'message_events', {
        message_send: 0,
        message_failure: 0,
        message_delivery: 0,
        message_click: 0,
        message_close: 0
      })
    }
  },

  mounted() {
    if (this.isCreatePage) {
      this.generateSignature()
    }
    this.secret = this.model.params.jwt_key
  },

  data() {
    let validateUrl = (rule, value, callback) => {
      if (isValidUrl(value)) {
        callback()
      } else {
        callback(new Error(this.urlValidationMessage))
      }
    }
    return {
      showToken: false,
      tokenError: false,
      secret: null,
      urlValidationMessage: this.$t('RestForm_FieldInvalidUrl'),
      rules: {
        name: {
          required: true,
          message: this.$t('RestForm_FieldIsRequired')
        },
        params: {
          url: [
            {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            },
            {
              validator: validateUrl
            }
          ],
          bearer_token: [
            {
              required: true,
              message: this.$t('RestForm_FieldIsRequired')
            }
          ],
          consent_url: [
            {
              required: false,
              message: this.$t('RestForm_FieldIsRequired')
            },
            {
              validator: validateUrl
            }
          ],
          consent_auth_url: [
            {
              required: false,
              message: this.$t('RestForm_FieldIsRequired')
            },
            {
              validator: validateUrl
            }
          ],
          consent_client_id: [
            {
              required: false,
              message: this.$t('RestForm_FieldIsRequired')
            }
          ],
          consent_client_secret: [
            {
              required: false,
              message: this.$t('RestForm_FieldIsRequired')
            }
          ]
        }
      },
      messageEventsList: [
        {
          code: 'message_send',
          name: 'Message Send'
        },
        {
          code: 'message_failure',
          name: 'Message Failure'
        },
        {
          code: 'message_click',
          name: 'Message Click'
        },
        {
          code: 'message_delivery',
          name: 'Message Delivery'
        },
        {
          code: 'message_close',
          name: 'Message Close',
          description: 'Applicable only for In-App channel'
        }
      ],
      messageTypesList: [
        {
          code: 'ios_push',
          name: this.$t('Integrations_Webhook_MessageType_iOSPush')
        },
        {
          code: 'android_push',
          name: this.$t('Integrations_Webhook_MessageType_AndroidPush')
        },
        {
          code: 'web_push',
          name: this.$t('Integrations_Webhook_MessageType_WebPush')
        },
        {
          code: 'inapp',
          name: this.$t('Integrations_Webhook_MessageType_InApp')
        }
      ]
    }
  },

  computed: {
    isCreatePage() {
      return !this.$route.query.id
    },
    isConsentSettingsEnabled() {
      return this.model.params.enable_consent === 1
    }
  },

  methods: {
    handleGenerateButton() {
      this.$confirm(this.$t('Integrations_Opap_Webhook_ConfirmMessage'), '', {
        confirmButtonText: this.$t(
          'Integrations_Opap_Webhook_ConfirmBtnConfirm'
        ),
        cancelButtonText: this.$t('Integrations_Opap_Webhook_ConfirmBtnCancel'),
        type: 'warning'
      })
        .then(() => {
          this.generateSignature()
        })
        .catch(() => {})
    },
    generateSignature() {
      RestApi.post(
        `projects/${this.model.project_id}/opapwebhook/jwt/generate-key`
      )
        .then(response => {
          let data = JSON.parse(response.data)
          this.model.params.jwt_key = data.key
          this.secret = data.key
          this.tokenError = false
        })
        .catch(error => {
          this.tokenError = error
        })
    }
  },

  watch: {
    model: {
      handler(val) {
        this.rules.params.consent_url[0].required = this.isConsentSettingsEnabled
        this.rules.params.consent_auth_url[0].required = this.isConsentSettingsEnabled
        this.rules.params.consent_client_id[0].required = this.isConsentSettingsEnabled
        this.rules.params.consent_client_secret[0].required = this.isConsentSettingsEnabled
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.el-form-item__content .icon {
  position: absolute;
  padding: 10px;
  top: 0;
  right: 0;
}

.el-form-item__content .blurred {
  -webkit-filter: blur(2px);
  -moz-filter: blur(2px);
  -ms-filter: blur(2px);
  -o-filter: blur(2px);
  filter: blur(2px);
}

.list-title {
  font-size: 15px;
  margin-bottom: 20px;
}
.token-wrapper {
  width: max-content;
}
/deep/ .el-input.is-disabled .el-input__inner {
  min-width: 550px;
}
</style>



// WEBPACK FOOTER //
// src/modules/integrations/types/opapwebhook/OpapWebhookSettings.vue